import { Button, Typography } from '@material-tailwind/react'
import { colors } from '@material-tailwind/react/types/generic'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useDarkMode } from '../contexts/DarkModeContext'

interface AgamiLibraryPaginationProps {
  page: number
  pageSize: number
  total: number
  onPageChange: (page: number) => void
}

export const AgamiLibraryPagination: React.FC<AgamiLibraryPaginationProps> = ({
  page,
  pageSize,
  total,
  onPageChange,
}) => {
  const { t } = useTranslation()
  const { darkMode } = useDarkMode()
  const buttonColor = darkMode ? 'white' : ('primary' as colors)
  const lastPage = Math.max(1, Math.ceil(total / pageSize))
  const pages = useMemo(() => {
    const start = Math.max(1, Math.min(page - 1, lastPage - 2))
    return Array.from({ length: Math.min(3, lastPage) }, (_, index) => start + index)
  }, [lastPage, page])

  if (total === 0) return null

  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-4">
      <Typography variant="small" className="dark:text-gray-200">
        {t('agamiLibrary.pagination.range', {
          from: (page - 1) * pageSize + 1,
          to: Math.min(page * pageSize, total),
          total,
        })}
      </Typography>
      <div className="flex items-center gap-2" aria-label={t('agamiLibrary.pagination.label')}>
        <Button
          size="sm"
          variant="outlined"
          color={buttonColor}
          disabled={page <= 1}
          aria-label={t('common.previous')}
          data-testid="agami-library-page-previous"
          onClick={() => onPageChange(page - 1)}
        >
          ‹
        </Button>
        {pages.map((value) => (
          <Button
            key={value}
            size="sm"
            variant={value === page ? 'filled' : 'outlined'}
            color={buttonColor}
            aria-current={value === page ? 'page' : undefined}
            data-testid={`agami-library-page-${value}`}
            onClick={() => onPageChange(value)}
          >
            {value}
          </Button>
        ))}
        <Button
          size="sm"
          variant="outlined"
          color={buttonColor}
          disabled={page >= lastPage}
          aria-label={t('common.next')}
          data-testid="agami-library-page-next"
          onClick={() => onPageChange(page + 1)}
        >
          ›
        </Button>
      </div>
    </div>
  )
}
